import { useEffect, useState } from "react";
import { Dialog, Flex, Text, Button, Spinner, Card, TextField, Select } from "@radix-ui/themes";
import { loadStripe } from "@stripe/stripe-js";
import { Elements, PaymentElement, useStripe, useElements } from "@stripe/react-stripe-js";
import { useAddPaymentMethod, useConfirmPaymentMethod } from "hooks/billing/useBilling";
import { useToast } from "../../context/ToastContext";
import params from "configs/params";

const stripePromise = loadStripe(params.stripePublishableKey);

const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);

const getInitialBillingState = (customer) => {
  const address = customer?.address || {};

  return {
    name: customer?.name || "",
    email: customer?.email || "",
    country: address.country || "GB",
    postalCode: address.postal_code || "",
    address: address.line1 || "",
    city: address.city || "",
  };
};

function AddPaymentMethodForm({ clientSecret, payingCustomerId, customer, countriesList, onClose, onBack, onProcessingChange }) {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [billingState, setBillingState] = useState(() => getInitialBillingState(customer));
  const { setToastMessage } = useToast();
  const confirmPaymentMethodMutation = useConfirmPaymentMethod();

  const setProcessingState = (value) => {
    setProcessing(value);
    onProcessingChange?.(value);
  };

  useEffect(() => {
    setBillingState(getInitialBillingState(customer));
  }, [customer, clientSecret]);

  const updateField = (field, value) => {
    setBillingState((current) => ({ ...current, [field]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    const trimmedName = billingState.name.trim();
    const trimmedEmail = billingState.email.trim();
    const trimmedPostalCode = billingState.postalCode.trim();
    const trimmedAddress = billingState.address.trim();
    const trimmedCity = billingState.city.trim();

    if (!billingState.country) {
      setError("Country is required for card payments.");
      return;
    }

    if (!trimmedName) {
      setError("Name is required for card payments.");
      return;
    }

    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      setError("A valid email is required for card payments.");
      return;
    }

    if (!trimmedPostalCode) {
      setError("Postal code is required for card payments.");
      return;
    }

    if (!trimmedAddress) {
      setError("Address is required for card payments.");
      return;
    }

    if (!trimmedCity) {
      setError("City is required for card payments.");
      return;
    }

    setProcessingState(true);
    setError(null);

    try {
      const { error: submitError } = await elements.submit();
      if (submitError) {
        // setError(submitError.message);
        setError("There was an error processing your payment details. Please check the form and try again.");
        setProcessingState(false);
        return;
      }

      const { error: confirmError, setupIntent } = await stripe.confirmSetup({
        elements,
        clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/organisation/billing`,
          payment_method_data: {
            billing_details: {
              name: trimmedName,
              email: trimmedEmail,
              address: {
                country: billingState.country,
                postal_code: trimmedPostalCode,
                line1: trimmedAddress,
                city: trimmedCity,
              },
            },
          },
        },
        redirect: "if_required",
      });

      if (confirmError) {
        // setError(confirmError.message || "Unable to confirm the payment method");
        setError("There was an error confirming your payment method. Please check the form and try again.");
        setProcessingState(false);
        return;
      }

      if (!setupIntent || setupIntent.status !== "succeeded") {
        setError("Payment method setup is incomplete. Please try again.");
        setProcessingState(false);
        return;
      }

      await confirmPaymentMethodMutation.mutateAsync({
        setup_intent_id: setupIntent.id,
        name: trimmedName,
        email: trimmedEmail,
        ...(payingCustomerId && { paying_customer_id: payingCustomerId }),
      });

      setToastMessage("Payment method updated successfully!");
      onClose();
    } catch (requestError) {
      // setError(requestError.response?.data?.error || "Failed to update payment method");
      setError("There was an error updating your payment method. Please try again.");
    } finally {
      setProcessingState(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <Flex direction="column" gap="4">
        <Flex direction="column" gap="2">
          <Text size="2" weight="medium">Billing Country</Text>
          <Select.Root value={billingState.country} onValueChange={(value) => updateField("country", value)}>
            <Select.Trigger placeholder="Select country" style={{ width: "100%" }} />
            <Select.Content style={{ maxHeight: "300px" }} position="popper">
              {countriesList.map((country) => (
                <Select.Item key={country.code} value={country.code}>
                  {country.name}
                </Select.Item>
              ))}
            </Select.Content>
          </Select.Root>
          <Text size="1" color="gray">
            We save this country to the Stripe customer with the replacement card.
          </Text>
        </Flex>

        <Flex direction="column" gap="2">
          <Text size="2" weight="medium">Billing Contact</Text>
          <TextField.Root
            placeholder="Name"
            value={billingState.name}
            onChange={(event) => updateField("name", event.target.value)}
            disabled={processing}
          />
          <TextField.Root
            type="email"
            placeholder="Email"
            value={billingState.email}
            onChange={(event) => updateField("email", event.target.value)}
            disabled={processing}
          />
          <TextField.Root
            placeholder="Postal Code"
            value={billingState.postalCode}
            onChange={(event) => updateField("postalCode", event.target.value)}
            disabled={processing}
          />
          <TextField.Root
            placeholder="Address"
            value={billingState.address}
            onChange={(event) => updateField("address", event.target.value)}
            disabled={processing}
          />
          <TextField.Root
            placeholder="City"
            value={billingState.city}
            onChange={(event) => updateField("city", event.target.value)}
            disabled={processing}
          />
          <Text size="1" color="gray">
            We store these details on the Stripe customer because the card form itself doesn’t collect them.
          </Text>
        </Flex>

        <Flex direction="column" gap="2">
          <Text size="2" weight="medium">Payment Details</Text>
          <div
            style={{
              padding: "12px",
              border: "1px solid var(--gray-6)",
              borderRadius: "var(--radius-2)",
              backgroundColor: "var(--color-background)",
            }}
          >
            <PaymentElement
              options={{
                layout: "accordion",
                defaultValues: {
                  billingDetails: {
                    address: {
                      country: billingState.country || "GB",
                    },
                  },
                },
              }}
            />
          </div>
          <Text size="1" color="gray">
            Choose a replacement card for future subscription invoices.
          </Text>
        </Flex>

        {error && <Text size="2" color="red">{error}</Text>}

        <Flex gap="3" mt="4" justify="end">
          {onBack && (
            <Button variant="soft" color="gray" type="button" onClick={onBack} disabled={processing}>
              Back
            </Button>
          )}
          <Button variant="soft" color="gray" type="button" onClick={onClose} disabled={processing}>
            Cancel
          </Button>
          <Button type="submit" disabled={!stripe || processing} loading={processing}>
            Update payment method
          </Button>
        </Flex>
      </Flex>
    </form>
  );
}

export function AddPaymentMethodModal({ open, onOpenChange, payingCustomerId, allowBacs = false, customer = null, countriesList = [] }) {
  const [clientSecret, setClientSecret] = useState(null);
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [error, setError] = useState(null);
  const [activeFlow, setActiveFlow] = useState(allowBacs ? null : "card");
  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const { mutateAsync: createPaymentMethodSetup } = useAddPaymentMethod();

  const resetState = () => {
    setClientSecret(null);
    setLoadingIntent(false);
    setError(null);
    setActiveFlow(allowBacs ? null : "card");
  };

  const handleClose = () => {
    if (paymentProcessing) return;
    resetState();
    onOpenChange(false);
  };

  const loadPaymentMethodSetup = async (paymentMethodType) => {
    setLoadingIntent(true);
    setError(null);
    setClientSecret(null);

    try {
      const result = await createPaymentMethodSetup({
        ...(payingCustomerId && { paying_customer_id: payingCustomerId }),
        payment_method_type: paymentMethodType,
      });

      const responseData = result.data || result;

      if (responseData.flow === "checkout") {
        if (!responseData.checkout_url) {
          throw new Error("Hosted setup URL was not returned");
        }

        window.location.href = responseData.checkout_url;
        return;
      }

      setClientSecret(responseData.setup_intent_client_secret || null);
      setActiveFlow("card");
    } catch (requestError) {
      setError(requestError.response?.data?.error || requestError.message || "Failed to load payment method setup");
      setActiveFlow(allowBacs ? null : "card");
    } finally {
      setLoadingIntent(false);
    }
  };

  const handleSelectCard = () => {
    setActiveFlow("card");
    loadPaymentMethodSetup("card");
  };

  const handleSelectBacs = () => {
    setActiveFlow("bacs_debit");
    loadPaymentMethodSetup("bacs_debit");
  };

  const handleBackToSelection = () => {
    setClientSecret(null);
    setLoadingIntent(false);
    setError(null);
    setActiveFlow(null);
  };

  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setLoadingIntent(false);
      setError(null);
      setActiveFlow(allowBacs ? null : "card");
      return;
    }

    if (allowBacs) {
      setClientSecret(null);
      setLoadingIntent(false);
      setError(null);
      setActiveFlow(null);
      return;
    }

    let disposed = false;

    const loadCardSetup = async () => {
      setActiveFlow("card");
      setLoadingIntent(true);
      setError(null);
      setClientSecret(null);

      try {
        const result = await createPaymentMethodSetup({
          ...(payingCustomerId && { paying_customer_id: payingCustomerId }),
          payment_method_type: "card",
        });

        if (disposed) {
          return;
        }

        const responseData = result.data || result;
        setClientSecret(responseData.setup_intent_client_secret || null);
      } catch (requestError) {
        if (!disposed) {
          setError(requestError.response?.data?.error || "Failed to load payment method setup");
        }
      } finally {
        if (!disposed) {
          setLoadingIntent(false);
        }
      }
    };

    loadCardSetup();

    return () => {
      disposed = true;
    };
  }, [open, allowBacs, payingCustomerId, createPaymentMethodSetup]);

  return (
    <Dialog.Root open={open} onOpenChange={(nextOpen) => (nextOpen ? onOpenChange(true) : handleClose())}>
      <Dialog.Content
        style={{ maxWidth: 560 }}
        onEscapeKeyDown={(event) => { if (paymentProcessing) event.preventDefault(); }}
        onPointerDownOutside={(event) => { if (paymentProcessing) event.preventDefault(); }}
        onInteractOutside={(event) => { if (paymentProcessing) event.preventDefault(); }}
      >
        <Dialog.Title>Update Payment Method</Dialog.Title>
        <Dialog.Description size="2" mb="4">
          Replace your current billing method. The new method becomes the default for future subscription invoices.
        </Dialog.Description>

        {!loadingIntent && !error && !clientSecret && allowBacs && activeFlow === null && (
          <Flex direction="column" gap="3">
            <Card>
              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text size="3" weight="medium">Card</Text>
                  <Text size="2" color="gray">
                    Update to a replacement card in this modal, with billing contact and address details.
                  </Text>
                </Flex>
                <Flex justify="end">
                  <Button onClick={handleSelectCard}>Use card</Button>
                </Flex>
              </Flex>
            </Card>

            <Card>
              <Flex direction="column" gap="3">
                <Flex direction="column" gap="1">
                  <Text size="3" weight="medium">UK bank account</Text>
                  <Text size="2" color="gray">
                    Bacs Direct Debit mandate setup opens in Stripe-hosted checkout for this account.
                  </Text>
                </Flex>
                <Flex justify="end">
                  <Button variant="soft" onClick={handleSelectBacs}>Use UK bank account</Button>
                </Flex>
              </Flex>
            </Card>

            <Flex justify="end">
              <Button variant="soft" color="gray" onClick={handleClose}>Close</Button>
            </Flex>
          </Flex>
        )}

        {loadingIntent && (
          <Flex direction="column" gap="3" align="center" justify="center" py="6">
            <Spinner />
            <Text size="2" color="gray">
              {activeFlow === "bacs_debit"
                ? "Redirecting to Stripe's secure Bacs setup..."
                : "Preparing secure payment form..."}
            </Text>
          </Flex>
        )}

        {!loadingIntent && error && (
          <Flex direction="column" gap="3">
            <Text size="2" color="red">{error}</Text>
            <Flex justify="end" gap="2">
              {allowBacs && (
                <Button variant="soft" color="gray" onClick={handleBackToSelection}>Back</Button>
              )}
              <Button variant="soft" color="gray" onClick={handleClose}>Close</Button>
            </Flex>
          </Flex>
        )}

        {!loadingIntent && !error && clientSecret && (
          <Elements key={clientSecret} stripe={stripePromise} options={{ clientSecret }}>
            <AddPaymentMethodForm
              clientSecret={clientSecret}
              payingCustomerId={payingCustomerId}
              customer={customer}
              countriesList={countriesList}
              onClose={handleClose}
              onBack={allowBacs ? handleBackToSelection : null}
              onProcessingChange={setPaymentProcessing}
            />
          </Elements>
        )}
      </Dialog.Content>
    </Dialog.Root>
  );
}
